import { Suspense, useEffect, useRef, useState } from 'react';
import { AnimatePresence, motion, useReducedMotion } from 'motion/react';
import { useLocation, useOutlet } from 'react-router-dom';
import { useLoading } from 'ra-core';
import { Loading } from '@/components/admin/loading';
import { EASE_PREMIUM, EASE_PREMIUM_IN, MOTION } from '@/lib/motion';

/**
 * Normalizes the pathname into a transition key. Routes that share a logical
 * page (e.g. tab-switcher URLs like `/publisher/:id/offers/active` ↔ `/publisher/:id/offers/finished`)
 * collapse to the same key so toggling tabs doesn't replay the transition.
 */
function getTransitionKey(pathname: string): string {
  const offerTabs = pathname.match(/^(\/publisher\/[^/]+\/offers)\/[^/]+$/);
  if (offerTabs) return offerTabs[1];
  return pathname;
}

/**
 * Sequenced route crossfade: the current page fades OUT, then the next page
 * fades IN — but the fade-in waits until the page's data has loaded, so it
 * animates the *fully-rendered* content instead of an empty shell that then
 * pops its content in mid-fade.
 *
 * Lives below `PublisherLayout` and renders `useOutlet()` (not a live
 * `<Outlet/>`) so the exiting page keeps its frozen route context — see
 * PublisherLayout for the why.
 */
export function PageTransition() {
  const location = useLocation();
  const outlet = useOutlet();
  const transitionKey = getTransitionKey(location.pathname);

  return (
    <AnimatePresence mode="wait">
      {/* Keyed per route so PageFade remounts and re-waits for each
          navigation's own data load. */}
      <PageFade key={transitionKey}>
        <Suspense fallback={<Loading />}>{outlet}</Suspense>
      </PageFade>
    </AnimatePresence>
  );
}

function PageFade({ children }: { children: React.ReactNode }) {
  const reduced = useReducedMotion();
  const isLoading = useLoading();

  // Hold at opacity 0 until the page's data settles, then fade the LOADED
  // content in — so the fade carries fully-rendered content instead of an
  // empty shell.
  //
  // The tricky part: `AnimatePresence mode="wait"` doesn't mount this page
  // until the previous one finishes exiting, so the destination's data query
  // hasn't registered yet at first commit — `isLoading` reads `false` for a
  // tick before it flips `true`. Naively flipping `ready` on that first
  // `false` latches the page visible immediately and the fade races ahead of
  // the content (it completes ~150ms before the content even mounts, so the
  // content visibly pops in). To avoid that we require a real load cycle:
  // once we've seen `isLoading` go `true`, the next `false` means the content
  // is ready. Pages that genuinely never load (fully cached) reveal after a
  // short grace window instead of waiting forever.
  const [ready, setReady] = useState(false);
  const sawLoading = useRef(false);
  useEffect(() => {
    if (isLoading) {
      sawLoading.current = true;
      // Safety net: never leave the page invisible indefinitely if some
      // background query never settles.
      const safety = setTimeout(() => setReady(true), 2500);
      return () => clearTimeout(safety);
    }
    // A load cycle we observed has now completed → content is rendered.
    if (sawLoading.current) {
      setReady(true);
      return;
    }
    // Not loading and we've never seen a load. Either the page is fully
    // cached, or the destination's query simply hasn't subscribed yet (the
    // mode="wait" race). Wait a couple frames: if a load starts this effect
    // re-runs and we hold; if nothing loads, treat it as cached and reveal.
    const grace = setTimeout(() => setReady(true), 100);
    return () => clearTimeout(grace);
  }, [isLoading]);

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: ready ? 1 : 0 }}
      exit={{
        opacity: 0,
        transition: {
          duration: reduced ? MOTION.reducedPage : MOTION.pageExit,
          ease: EASE_PREMIUM_IN,
        },
      }}
      transition={{
        // Same curve as the inner section Reveals (EASE_PREMIUM), so the route
        // fade and the staggered cascade read as one bloom rather than two
        // different motions. The sections' rise masks out-expo's front-loaded
        // opacity, so the pure page fade no longer needs a separate ease.
        duration: reduced ? MOTION.reducedPage : MOTION.pageEnter,
        ease: EASE_PREMIUM,
      }}
      className="flex flex-1 flex-col min-h-0"
    >
      {children}
    </motion.div>
  );
}
